export interface DatePickerAnchorOptions {
  /** Distance between the field and the panel, in pixels. */
  gap?: number;
  /** Smallest allowed distance from the viewport edge, in pixels. */
  margin?: number;
}

interface DotNetReference {
  invokeMethodAsync(method: string): Promise<unknown>;
}

/**
 * Anchors a calendar panel to its field and renders it in the browser's top layer.
 *
 * Absolute positioning alone is not enough: a date picker normally sits inside a `.card`,
 * and the card clips its content to keep the rounded corners, so the panel was cut off at
 * the card's edge and clicks landed on the content underneath. The native popover API
 * lifts the panel out of every clipping ancestor without moving the node, which matters
 * because the node belongs to a framework renderer.
 *
 * Because the top layer is positioned against the viewport, the panel no longer follows
 * the field on its own; this controller keeps the two together on scroll and resize.
 *
 * Dismissal also lives here. A top-layer panel takes no focus when the user clicks the
 * page behind it, so a focusout handler never fires and the calendar would stay open;
 * the outside press is detected on the document instead and reported back to the caller.
 */
export class DatePickerAnchor {
  private static instances = new Map<HTMLElement, DatePickerAnchor>();

  // Definite assignment: the constructor may return a cached instance before these
  // are set, which TypeScript cannot see.
  private root!: HTMLElement;
  private field!: HTMLElement;
  private panel!: HTMLElement;
  private options!: Required<DatePickerAnchorOptions>;
  private dotnet?: DotNetReference;
  private listeners = new AbortController();
  private frame = 0;
  private disposed = false;

  constructor(root: HTMLElement, options: DatePickerAnchorOptions = {}, dotnet?: DotNetReference) {
    const existing = DatePickerAnchor.instances.get(root);
    if (existing) return existing;

    const field = root.querySelector<HTMLElement>('.antumo-datepicker-trigger');
    const panel = root.querySelector<HTMLElement>('.antumo-datepicker-panel');
    if (!field || !panel) {
      throw new Error('DatePickerAnchor requires a trigger and a panel.');
    }

    this.root = root;
    this.field = field;
    this.panel = panel;
    this.options = { gap: options.gap ?? 4, margin: options.margin ?? 8 };
    this.dotnet = dotnet;

    DatePickerAnchor.instances.set(root, this);
    this.show();
    this.bind();
  }

  static getInstance(root: HTMLElement): DatePickerAnchor | null {
    return this.instances.get(root) ?? null;
  }

  /** Opens the panel for the wrapper with this id. Safe to call when already open. */
  static anchorById(id: string, dotnet?: DotNetReference, options: DatePickerAnchorOptions = {}): void {
    const root = document.getElementById(id);
    if (!root) return;
    const existing = this.instances.get(root);
    if (existing) { existing.reposition(); return; }
    // The panel is rendered by the framework one frame after the state change.
    requestAnimationFrame(() => {
      const current = document.getElementById(id);
      if (!current) return;
      try { new DatePickerAnchor(current, options, dotnet); } catch { /* panel not rendered yet */ }
    });
  }

  /** Closes and forgets the panel for this id. */
  static disposeById(id: string): void {
    const root = document.getElementById(id);
    if (!root) return;
    this.instances.get(root)?.dispose();
  }

  private bind(): void {
    const { signal } = this.listeners;
    const reposition = () => this.schedule();
    // Capture phase: the field may sit inside its own scrolling region.
    window.addEventListener('scroll', reposition, { capture: true, signal });
    window.addEventListener('resize', reposition, { signal });
    window.visualViewport?.addEventListener('resize', reposition, { signal });
    window.visualViewport?.addEventListener('scroll', reposition, { signal });

    // pointerdown, not click: a press that starts outside should dismiss even when the
    // pointer is released elsewhere, and the field's own click must not immediately
    // reopen what this handler just closed.
    document.addEventListener('pointerdown', event => {
      const target = event.target as Node | null;
      if (target && this.root.contains(target)) return;
      this.requestDismiss();
    }, { capture: true, signal });

    // Escape from anywhere: the panel takes no focus, so the wrapper's own key handler
    // would never see the key after an outside click.
    document.addEventListener('keydown', event => {
      if ((event as KeyboardEvent).key === 'Escape') this.requestDismiss();
    }, { capture: true, signal });
  }

  /** Tells the owning component to close. The component owns the open state. */
  private requestDismiss(): void {
    if (this.disposed) return;

    if (this.dotnet) {
      this.dotnet.invokeMethodAsync('OnDismissed').catch(() => {
        // The circuit may be closing; the panel goes away with it.
      });
      return;
    }

    // No .NET reference: the panel belongs to a component that lives entirely
    // in the browser, which listens for this instead.
    this.root.dispatchEvent(
      new CustomEvent('antumo:datepicker:dismiss', { bubbles: true })
    );
  }

  private show(): void {
    if (typeof this.panel.showPopover === 'function' && !this.panel.matches(':popover-open')) {
      try { this.panel.showPopover(); } catch { /* already open in another stack */ }
    }
    this.reposition();
  }

  private schedule(): void {
    if (this.disposed || this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.reposition(); });
  }

  /** Places the panel under the field, flipping above it when the space below runs out. */
  reposition(): void {
    if (this.disposed || !this.root.isConnected) return;

    const viewport = window.visualViewport;
    const vw = viewport?.width ?? window.innerWidth;
    const vh = viewport?.height ?? window.innerHeight;
    const vx = viewport?.offsetLeft ?? 0;
    const vy = viewport?.offsetTop ?? 0;
    const { gap, margin } = this.options;

    const anchor = this.field.getBoundingClientRect();
    const bounds = this.panel.getBoundingClientRect();

    const below = vy + vh - margin - anchor.bottom - gap;
    const above = anchor.top - vy - margin - gap;
    const flip = below < bounds.height && above > below;

    let y = flip ? anchor.top - gap - bounds.height : anchor.bottom + gap;
    let x = anchor.left;

    x = Math.max(vx + margin, Math.min(x, vx + vw - margin - bounds.width));
    y = Math.max(vy + margin, Math.min(y, vy + vh - margin - bounds.height));

    this.panel.style.left = `${x}px`;
    this.panel.style.top = `${y}px`;
    this.panel.dataset.datepickerPlacement = flip ? 'top' : 'bottom';
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.listeners.abort();
    cancelAnimationFrame(this.frame);
    if (typeof this.panel.hidePopover === 'function' && this.panel.matches(':popover-open')) {
      try { this.panel.hidePopover(); } catch { /* already detached */ }
    }
    DatePickerAnchor.instances.delete(this.root);
  }
}
